import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { useListClickHandlers } from './functions/useListClickHandlers';
import { ListProps, ListRowType, SingleFilter } from 'types/ListTypes';
import ListContext from 'components/lists/ListContext';
import PrimaryButton from 'components/buttons/PrimaryButton';
import FilterButton from './FilterButton';
import IconButton from 'components/buttons/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';
import TabsButton from 'components/buttons/TabsButton';
import TabSelect from 'components/forms/basefields/TabSelect';
import WeekNavigation from 'components/layout/WeekNavigation';
import SecondaryButton from 'components/buttons/SecondaryButton';
import FilterBar from './FilterBar';
import ListMoreMenu from './ListMoreMenu';

const ListButtons = <T extends ListRowType>({
    query, objectName, filters,
    filterFields,
    activeFilters,
    setActiveFilters,
    filterDropdownData,
    bulkActions,
    moreMenu,
    inactiveItemsActive,
    onToggleInactiveItems,
    onExportAll,
    showAddButton = true, showSearch, showImport, showExport, showPerWeek, weekNavigation, showColumnSelector, postNewItem, 
    backButton, headerTitle, openPageUrl, formModal, detailModal, modalSize, buttonSize = 'small', quickFilterSize = 'large', changeViewButton, activeQuickFilter, actionButton,
    secondaryButton, refetchList, renderHeaderInfo, renderTrailingInfo, getFormModal, getModalSize, getFormModalTitle, onActiveQuickFilterChange,
    tableWidthClass
}: ListProps<T>) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { userLocale, userTimezone } = useSettings();
    const hasRightCheck = useAllowedRight;
    const { searchTerm, loading, extraMeta, handleSearchChange } = useContext(ListContext);
    const { handlePrimaryButtonClick, handleSecondaryButtonClick, isPostingNewItem } = useListClickHandlers({objectName, postNewItem, openPageUrl, formModal: getFormModal ? getFormModal(extraMeta) : formModal, formModalTitle: getFormModalTitle ? getFormModalTitle(extraMeta) : undefined, detailModal, modalSize: getModalSize ? getModalSize(extraMeta) : modalSize, userLocale, userTimezone, refetchList});

    // Get the quick filters from the filters
    const quickFilters = Array.isArray(filters) ? (filters as SingleFilter[]).filter(f => f.quickFilter) : [];
    const buttonFilters = Array.isArray(filters) ? (filters as SingleFilter[]).filter(f => !f.quickFilter) : [];

    // Determine to show the list button container if no button feature is active
    let showListButtonContainer = true
    if (!showPerWeek && !backButton && !showAddButton && !showSearch && (!showImport && !showExport || !hasRightCheck('can_export')) && !showColumnSelector && !filters
        && !(filterFields && filterFields.length > 0) && !renderHeaderInfo && !renderTrailingInfo) {
        showListButtonContainer = false;
    }

    // Render the view switch of the list, at the start of the buttons or after the filter fields
    const renderChangeViewButton = () => changeViewButton && (
        <TabsButton
            buttons={changeViewButton.buttons}
            currentTabValue={changeViewButton.currentView}
            onTabsClick={(view) => changeViewButton.onViewChange(view)}
            size={changeViewButton.buttonSize ?? 'medium'}
        />
    );

    const handleActionClick = async () => {
        if (!actionButton || !refetchList) return
        await actionButton.onClick(refetchList)
    }

    const handleSecondaryClick = async () => {
        if (!secondaryButton || !refetchList) return
        await secondaryButton.onClick(refetchList)
    }

    return (
        showListButtonContainer ? (
            <div className={`${tableWidthClass} list-button-wrapper`}>
                <div className='button-container'>
                    {showAddButton ? (
                        <PrimaryButton
                            onClick={handlePrimaryButtonClick}
                            onlyViewRestriction={true}
                            label={t('button.plus_add_object_label', { object_name: t(`${objectName}.singular`) })}
                            loading={isPostingNewItem}
                            size={buttonSize}
                        />
                    ) : backButton ? (
                        <div className="list-back-header">
                            <span className="back-button"
                                  onClick={() => {
                                if (backButton.url) {
                                    history.push(backButton.url);
                                } else {
                                    history.goBack();
                                }
                            }}>
                                <FontAwesomeIcon icon={faAngleLeft} />
                            </span>

                            <h2 className="fixed-title">
                                {t(backButton.title)}
                            </h2>
                        </div>
                    ) : headerTitle ? (
                        <h2 className="header-title">
                            {t(headerTitle)}
                        </h2>
                    ) : null}
                    {loading === 'show-loader' && <div className="loader"></div>}
                </div>
                <div className='button-container right-buttons'>
                    {renderHeaderInfo && (
                        <div className='header-info-text'>
                            {renderHeaderInfo(extraMeta)}
                        </div>
                    )}
                    {showSearch && (
                        <div className='search-container' style={{ marginTop: '2px' }}>
                            <input
                                type='text'
                                placeholder={t('forms.search_placeholder')}
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                        </div>
                    )}
                    {changeViewButton && changeViewButton.placement !== 'end' && renderChangeViewButton()}
                    {showPerWeek && weekNavigation && (
                        <WeekNavigation
                            weekStartDate={weekNavigation.weekStartDate}
                            onChange={weekNavigation.setWeekStartDate}
                            reverse={true}
                            buttonSize='medium'
                        />
                    )}
                    {actionButton && (
                        <PrimaryButton
                            onClick={handleActionClick}
                            label={actionButton.label}
                            tooltipText={actionButton.tooltip}
                            size={buttonSize} 
                        />
                    )}
                    {secondaryButton && (
                        secondaryButton.icon ? (
                            <IconButton
                                onClick={handleSecondaryClick}
                                label={secondaryButton.label}
                                tooltipText={secondaryButton.tooltip}
                                loading={secondaryButton.loading}
                                icon={secondaryButton.icon}
                                size={buttonSize} 
                            />
                        ) : (
                            <SecondaryButton
                                onClick={handleSecondaryClick}
                                label={secondaryButton.label}
                                tooltipText={secondaryButton.tooltip}
                                loading={secondaryButton.loading}
                                size={buttonSize} 
                            />
                        )
                    )}
                    {quickFilters.length > 0 && (
                        <TabSelect<SingleFilter>
                            options={quickFilters}
                            selectedOption={activeQuickFilter}
                            optionFormat='label'
                            onChange={(selectedOption) => onActiveQuickFilterChange?.(selectedOption)}
                            buttonSize={quickFilterSize}
                        />
                    )}
                    {buttonFilters.length > 0 &&
                        <FilterButton 
                            filters={buttonFilters} 
                            size={buttonSize} 
                            defaultParams={query?.defaultParams} 
                        />
                    }
                    {filterFields && filterFields.length > 0 && (
                        <FilterBar
                            filters={filterFields}
                            activeFilters={activeFilters ?? {}}
                            setActiveFilters={setActiveFilters!}
                            dropdownData={filterDropdownData ?? {}}
                        />
                    )}
                    {changeViewButton && changeViewButton.placement === 'end' && renderChangeViewButton()}
                    {filterFields && filterFields.length > 0 && (
                        <ListMoreMenu
                            bulkActions={bulkActions}
                            moreMenu={moreMenu}
                            inactiveItemsActive={inactiveItemsActive}
                            onToggleInactiveItems={onToggleInactiveItems}
                            onExportAll={onExportAll}
                            size={buttonSize}
                        />
                    )}
                    {showImport || (showExport && hasRightCheck('can_export')) ? (
                        <div className='import-export-buttons'>
                            <IconButton
                                onClick={() => (history.push(`/import-data/${query.object}/`))}
                                icon={<FontAwesomeIcon icon={faCloudArrowUp} />}
                                tooltipText='button.import_button_label'
                                size="medium" />
                        
                            {/* <SecondaryButton
                                onClick={handleSecondaryButtonClick} 
                                size={buttonSize} 
                                label={showImport && (showExport && hasRightCheck('can_export')) ? t('button.import_export_button_label') : showImport ? t('button.import_button_label') : t('button.export_button_label')} /> */}
                        </div>
                    ) : null }
                    {renderTrailingInfo && (
                        <div className='header-info-text'>
                            {renderTrailingInfo(extraMeta)}
                        </div>
                    )}
                </div>
            </div>
        ) : (
            null
        )
    );
};

export default ListButtons;